import { PressableScale } from "pressto";
import { View, Text, Card } from "@src/components";
import { useUnistyles } from "react-native-unistyles";
import type { ArmedResponse } from "@src/services/api/types";
import { Icon, type IconNameType } from "@src/components/Icon";

interface ResponsePreviewProps {
  response: ArmedResponse | null | undefined;
  onPress?: () => void;
  accessoryIcon?: IconNameType;
}

export function ResponsePreview({ response, onPress, accessoryIcon }: ResponsePreviewProps) {
  const { theme } = useUnistyles();
  const hasNoResponse = !response;

  // Show warning state if no response is configured
  if (hasNoResponse) {
    const cardContent = (
      <Card
        borderColor="warning"
        py={12}
        style={{
          borderColor: theme.colors.warning[500],
          backgroundColor: theme.colors.warning[50],
        }}
      >
        <View flexDirection="row" alignItems="center" gap="md">
          <View style={{ minWidth: 80 }}>
            <Text textStyle="sm/medium" style={{ color: theme.colors.warning[900] }}>
              Response
            </Text>
          </View>
          <View style={{ flex: 1, minWidth: 0 }} alignItems="flex-end">
            <View flexDirection="row" alignItems="center" gap="md" justifyContent="flex-end">
              <Icon name="warningOutline" color={theme.colors.warning[600]} />
              <Text
                textStyle="sm/regular"
                numberOfLines={2}
                textAlign="right"
                style={{ color: theme.colors.warning[700], flexShrink: 1 }}
              >
                Not Configured
              </Text>
              {onPress && accessoryIcon && <Icon name={accessoryIcon} color={theme.colors.warning[600]} />}
            </View>
          </View>
        </View>
      </Card>
    );

    if (onPress) {
      return <PressableScale onPress={onPress}>{cardContent}</PressableScale>;
    }

    return cardContent;
  }

  // Show normal response state
  const enabledFeatures = [
    response.strobes === "on" && "Strobes",
    response.sirens === "on" && "Sirens",
    response.monitoring === "on" && "Monitoring",
    response.recording === "on" && "Recording",
  ]
    .filter(Boolean)
    .join(", ");

  const cardContent = (
    <Card py={12}>
      <View flexDirection="row" alignItems="center" gap="md">
        <View style={{ minWidth: 80 }}>
          <Text textStyle="sm/medium">Response</Text>
        </View>
        <View style={{ flex: 1, minWidth: 0 }} alignItems="flex-end">
          <View flexDirection="row" alignItems="center" gap="md" justifyContent="flex-end">
            <Text
              color="textMuted"
              textStyle="sm/regular"
              numberOfLines={2}
              textAlign="right"
              style={{ flexShrink: 1 }}
            >
              {enabledFeatures || "No features enabled"}
            </Text>
            {onPress && accessoryIcon && <Icon name={accessoryIcon} color={theme.colors.icon} />}
          </View>
        </View>
      </View>
    </Card>
  );

  if (onPress) {
    return <PressableScale onPress={onPress}>{cardContent}</PressableScale>;
  }

  return cardContent;
}
